import {useEffect, useRef, useState} from 'react';
import {ConversationDto, TranscriptDetails} from './InvestigateChatPanel';
import {
  useCreateInvestigateConversation,
  useDeleteInvestigateConversation,
  useGetTranscriptDetails,
} from '../../../../hooks/ai/investigate/useInvestigate';
import {ICreateConversationResponse} from '../../../../api/types/aiInvestigate.type';

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const conversationResponseToConversationDto = (
  response: ICreateConversationResponse
): ConversationDto => {
  return {
    conversationId: response.conversationId.toString(),
    conversationNumber: response.conversationNumber ?? 0,
    question: response.question,
    answer: response.answer,
    suggestedQuestions: response.followUpQuestions.map((question) => ({
      id: question.number,
      question: question.question,
    })),
    createDate: response.questionDateTime,
    updateDate: response.questionDateTime,
    isRemoved: response.isDeleted,
    isStreaming: false,
  };
};

type UseConversationProps = {
  transcriptDetails: TranscriptDetails;
  onConversationUpdate?: (response: ICreateConversationResponse) => void;
};

export const useConversation = ({
  transcriptDetails,
  onConversationUpdate,
}: UseConversationProps) => {
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isInactiveIndex, setIsInactiveIndex] = useState(false);

  const {data: transcriptDetailsResponse, isFetching: isTranscriptLoading} =
    useGetTranscriptDetails(transcriptDetails?.transcriptId, {
      enabled: transcriptDetails?.transcriptId > 0,
    });

  const temporaryConversationId = useRef<string>(null);
  const conversationCounterRef = useRef(1);
  const currentTranscriptIdRef = useRef(transcriptDetails?.transcriptId);
  const requestTranscriptIdRef = useRef<number | null>(null);

  const {mutate: createInvestigateConversation} =
    useCreateInvestigateConversation(
      (response: ICreateConversationResponse) => {
        onConversationCreated(response);
        onConversationUpdate?.(response);
      },
      (error: Error) => {
        onConversationError(error);
      }
    );

  const {mutateAsync: deleteConversationAsync} =
    useDeleteInvestigateConversation();

  const deleteConversation = async (conversationId: string) => {
    if (!transcriptDetails?.transcriptId) return;
    await deleteConversationAsync({
      transcriptId: transcriptDetails.transcriptId,
      conversationId,
    });
  };

  const onConversationError = async (error: Error) => {
    await delay(1500); //common delay for smooth rendering
    if (requestTranscriptIdRef.current !== currentTranscriptIdRef.current)
      return;
    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.conversationId !== temporaryConversationId.current)
          return conv;
        return {
          ...conv,
          question: '',
          isError: true,
          answer: '',
          isStreaming: false,
        };
      })
    );
    setIsError(true);
    setLoading(false);
    setIsStreaming(false);
    temporaryConversationId.current = null;
  };

  const onConversationCreated = async (
    response: ICreateConversationResponse
  ) => {
    const conversationDto = conversationResponseToConversationDto(response);

    await delay(1500); //common delay for smooth rendering
    if (requestTranscriptIdRef.current !== currentTranscriptIdRef.current)
      return;
    conversationDto.conversationNumber = conversationCounterRef.current;
    conversationCounterRef.current++;
    setConversations((pv) => [
      ...pv.filter((c) => c.conversationId !== temporaryConversationId.current),
      conversationDto,
    ]);
    temporaryConversationId.current = null;

    setLoading(false);
    setIsStreaming(false);
    setIsError(false);
  };
  useEffect(() => {
    currentTranscriptIdRef.current = transcriptDetails?.transcriptId;
    if (!transcriptDetails?.transcriptId) {
      setConversations([]);
      setLoading(false);
      setIsStreaming(false);
      setIsError(false);
      setIsInactiveIndex(false);
      conversationCounterRef.current = 1;
      return;
    }

    setConversations([]);
    setLoading(false);
    setIsStreaming(false);
    setIsError(false);
    setIsInactiveIndex(false);
  }, [transcriptDetails?.transcriptId]);

  useEffect(() => {
    if (!transcriptDetailsResponse) return;

    const mappedConversations: ConversationDto[] = (
      transcriptDetailsResponse.conversations ?? []
    ).map((conv) => ({
      conversationId: String(conv.conversationId),
      conversationNumber: conv.conversationNumber,
      question: conv.question,
      answer: conv.answer,
      suggestedQuestions:
        transcriptDetailsResponse.latestConversationFollowUpQuestions?.map(
          (fq) => ({
            id: fq.number,
            question: fq.question,
          })
        ) ?? [],
      createDate: conv.questionDateTime,
      updateDate: conv.questionDateTime,
      isRemoved: conv.isDeleted,
      isError: false,
      isStreaming: false,
    }));

    setConversations(mappedConversations);
    setLoading(false);
    setIsStreaming(false);
    setIsError(false);
    conversationCounterRef.current =
      transcriptDetailsResponse.latestConversationNumber + 1;

    setIsInactiveIndex(!transcriptDetailsResponse.isIndexActive);
  }, [transcriptDetailsResponse]);

  const askQuestion = async (question: string) => {
    setIsError(false);
    setLoading(true);
    setIsStreaming(false);

    temporaryConversationId.current = Date.now().toString();
    const conversationNumber = conversationCounterRef.current;
    const nowIsoDate = new Date().toISOString();

    const newConversation: ConversationDto = {
      conversationId: temporaryConversationId.current,
      conversationNumber,
      question,
      answer: '',
      suggestedQuestions: [],
      createDate: nowIsoDate,
      updateDate: nowIsoDate,
      isError: false,
      isRemoved: false,
      isStreaming: false,
    };

    setConversations((prev) => [...prev, newConversation]);
    requestTranscriptIdRef.current = transcriptDetails.transcriptId;
    createInvestigateConversation({
      transcriptId: transcriptDetails.transcriptId,
      payload: {
        question,
      },
    });
  };

  return {
    conversations,
    setConversations,
    loading,
    isTranscriptLoading,
    isStreaming,
    isError,
    askQuestion,
    deleteConversation,
    isInactiveIndex,
  };
};
